/* eslint-disable multiline-ternary */
import React, { useEffect, useRef, useState } from 'react'
import { isAndroid, isIos } from '@braintree/browser-detection'
import { IVideoChapter, IVideoData } from '../@types/data'
import { useWindowSize } from '../context/WindowSizeContext'
import useDebounce from '../helpers/useDebounce'
import VideoChapter from './VideoChapter'
import { useData } from '../context/DataContext'

interface IProps {
  videoEl: HTMLVideoElement
  video: IVideoData
  currentTime: number
  onPlayPause: () => void
  onSeeking: (isSeeking: boolean) => void
}
const MARGIN = 6

const VideoControls: React.FC<IProps> = ({
  videoEl,
  video,
  currentTime,
  onPlayPause,
  onSeeking,
}: IProps) => {
  const { chapters } = video
  const containerRef = useRef<HTMLDivElement>(null)
  const touchDown = useRef<boolean>(false)
  const containerWidth = useRef<number>(0)
  const offsetLeft = useRef<number>(0)
  const videoDuration = useRef<number>(0)
  const seekTime = useRef<number>(-1)
  const [isSeeking, setSeeking] = useState(false)
  const [availableWidth, setAvailableWidth] = useState(0)
  const debouncedValue = useDebounce<number>(availableWidth, 500)
  const { dimensions } = useWindowSize()
  const isMobile = isAndroid() || isIos()
  const {
    selectedVideo,
    activeChapter,
    setActiveChapter,
    requestedChapter,
    setRequestedChapter,
    startAutoplayTimer,
    resetTimer,
  } = useData()

  useEffect(() => {
    if (isMobile) {
      window.addEventListener('touchmove', handleTouchMove)
      window.addEventListener('touchend', handleTouchEnd)
    } else {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseEnd)
    }

    return () => {
      if (isMobile) {
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchend', handleTouchEnd)
      } else {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseEnd)
      }
    }
  }, [])

  useEffect(() => {
    if (video.chapters.length > 0) {
      const chapter = video.chapters[0]
      setRequestedChapter(chapter)
      setActiveChapter(chapter)
    }
  }, [video])

  useEffect(() => {
    if (containerRef?.current == null) return
    const { width } = containerRef.current.getBoundingClientRect()
    containerWidth.current = width
    videoDuration.current = video.duration
    const realWidth = width - (chapters.length - 1) * MARGIN
    setAvailableWidth(realWidth)

    setTimeout(() => {
      if (containerRef?.current == null) return
      const { left } = containerRef.current.getBoundingClientRect()
      offsetLeft.current = left
    }, 700)
  }, [video, dimensions.width])

  useEffect(() => {
    if (isSeeking) return
    if (activeChapter !== undefined && currentTime >= activeChapter.time.end) {
      videoEl.pause()
      startAutoplayTimer()
    }
  }, [currentTime])

  useEffect(() => {
    if (!isSeeking) {
      video.chapters.forEach((chapter) => {
        if (
          chapter !== activeChapter &&
          currentTime > chapter.time.start &&
          currentTime <= chapter.time.end
        ) {
          // should be active
          if (chapter !== requestedChapter) {
            resetTimer()
            setRequestedChapter(chapter)
          }
        }
      })
    } else {
      resetTimer()
    }
  }, [isSeeking])

  useEffect(() => {
    if (requestedChapter === activeChapter) {
      videoEl.play().catch(() => {
        console.warn('playing video failed')
      })
    }
  }, [requestedChapter, activeChapter])

  const handleTouchStart = (e: React.TouchEvent): void => {
    if (e.touches.length > 1) return
    const currentX = e.touches[0].clientX
    let percentage = (currentX - offsetLeft.current) / containerWidth.current
    if (percentage < 0) percentage = 0
    if (percentage > 1) percentage = 1
    seekTime.current = videoDuration.current * percentage
    touchDown.current = true
    onSeeking(true)
    setSeeking(true)
    videoEl.pause()
  }

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (isMobile) return
    const currentX = e.clientX
    let percentage = (currentX - offsetLeft.current) / containerWidth.current
    if (percentage < 0) percentage = 0
    if (percentage > 1) percentage = 1
    seekTime.current = videoDuration.current * percentage
    touchDown.current = true
    onSeeking(true)
    setSeeking(true)
    videoEl.pause()
  }

  const handleTouchMove = (e: TouchEvent): void => {
    if (!touchDown.current || e.touches.length > 1) return
    const currentX = e.touches[0].clientX
    let percentage = (currentX - offsetLeft.current) / containerWidth.current
    if (percentage < 0) percentage = 0
    if (percentage > 1) percentage = 1
    seekTime.current = videoDuration.current * percentage
    handleChapterSeek(seekTime.current)
  }

  const handleMouseMove = (e: MouseEvent): void => {
    if (!touchDown.current) return
    const currentX = e.clientX
    let percentage = (currentX - offsetLeft.current) / containerWidth.current
    if (percentage < 0) percentage = 0
    if (percentage > 1) percentage = 1
    seekTime.current = videoDuration.current * percentage
    handleChapterSeek(seekTime.current)
  }

  const handleTouchEnd = (): void => {
    if (!touchDown.current) return
    touchDown.current = false
    handleChapterSeek(seekTime.current)
    seekTime.current = -1
    onSeeking(false)
    setTimeout(() => {
      setSeeking(false)
    }, 100)
    videoEl.play().catch(() => {
      console.warn('playing video failed')
    })
  }

  const handleMouseEnd = (e: MouseEvent): void => {
    if (!touchDown.current) return
    const currentX = e.clientX
    let percentage = (currentX - offsetLeft.current) / containerWidth.current
    if (percentage < 0) percentage = 0
    if (percentage > 1) percentage = 1
    seekTime.current = videoDuration.current * percentage
    handleChapterSeek(seekTime.current)
    touchDown.current = false
    seekTime.current = -1
    onSeeking(false)
    setTimeout(() => {
      setSeeking(false)
    }, 100)
    videoEl.play().catch(() => {
      console.warn('playing video failed')
    })
  }

  const handleChapterSeek = (value: number): void => {
    videoEl.currentTime = value
  }

  return (
    <>
      <button
        className="hidden w-12 h-12 mt-[5px] mr-2 md:block"
        onClick={onPlayPause}
      >
        {videoEl.paused ? (
          <svg
            className="w-full h-full"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
          >
            <path
              className="fill-gray-medium"
              d="M46 32L26 47V17L46 32Z"
              fill="#C4C4C4"
            />
          </svg>
        ) : (
          <svg
            className="w-full h-full"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="fill-gray-medium"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M23.6667 17C22.7462 17 22 17.7462 22 18.6667V45.3333C22 46.2538 22.7462 47 23.6667 47H25.3333C26.2538 47 27 46.2538 27 45.3333V18.6667C27 17.7462 26.2538 17 25.3333 17H23.6667ZM38.6667 17C37.7462 17 37 17.7462 37 18.6667V45.3333C37 46.2538 37.7462 47 38.6667 47H40.3333C41.2538 47 42 46.2538 42 45.3333V18.6667C42 17.7462 41.2538 17 40.3333 17H38.6667Z"
              fill="#C4C4C4"
            />
          </svg>
        )}
      </button>
      <div
        ref={containerRef}
        className="stat-slider flex-1 items-center select-none cursor-pointer"
        onTouchStart={handleTouchStart}
        onMouseDown={handleMouseDown}
        data-dtm={selectedVideo?.key}
      >
        <div className="relative flex flex-1 items-end justify-center min-h-[32px] lg:[min-h-[36px]">
          <div className="absolute w-full flex justify-between">
            {chapters.map((chapter: IVideoChapter, index) => (
              <VideoChapter
                key={`chapter-${index}`}
                data={chapter}
                currentTime={currentTime}
                seekTime={seekTime.current}
                width={Math.round(
                  ((chapter.time.end - chapter.time.start) /
                    videoDuration.current) *
                    debouncedValue
                )}
                first={index === 0}
                last={index === chapters.length - 1}
              />
            ))}
          </div>
          <div
            className="absolute top-[22px] -left-2 w-4 h-4 bg-gold rounded-full"
            style={{
              transform: `translateX(${
                (currentTime / videoDuration.current) * containerWidth.current
              }px)`,
            }}
          />
        </div>
        <div className="flex mt-2 xl:mt-3">
          <h6 className="mr-2 text-xs xl:text-sm uppercase text-gold tracking-wider">
            {activeChapter?.label}
          </h6>
          {/* <p className="text-gold text-xs xl:text-sm tracking-wide">
            {sectionLabel}
          </p> */}
        </div>
      </div>
    </>
  )
}

export default VideoControls
